import {
  Component,
  OnDestroy,
  OnInit,
  DOCUMENT,
  inject,
  ChangeDetectionStrategy,
} from "@angular/core";
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from "@angular/forms";
import { Router, ActivatedRoute } from "@angular/router";
import { NgxPermissionsService } from "ngx-permissions";

import { Subscription } from "rxjs";

import { UserRepository } from "../user.repository";
import { UserService } from "../user.service";
import { formType } from "./form-type.const";
import { User } from "../user.model";
import { NotifyService } from "../../notify/notify.service";
import { PasswordValidator } from "./password.validator";
import { TOAST } from "../../notify/toast/toast-type.enum";
import { BackendErrorPipe } from "../../shared/backend/backend-error.pipe";
import { noop } from "../../shared/noop.function";
import { AuthHttpConfig } from "../../security/authorization/auth-http-config";
import { ReturnUrlService } from "../../security/authorization/return-url.service";
import { role } from "../role.const";
import { Bind } from "primeng/bind";
import { ButtonDirective } from "primeng/button";
import { InputText } from "primeng/inputtext";
import { InputPassword } from "primeng/inputpassword";
import { IconField } from "primeng/iconfield";
import { InputIcon } from "primeng/inputicon";
import { Message } from "primeng/message";
import { Checkbox } from "primeng/checkbox";
import { SignInHeading, signInHeading } from "./sign-in-heading.const";

@Component({
  selector: "app-login",
  templateUrl: "./login.component.html",
  styleUrls: ["./login.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ButtonDirective,
    FormsModule,
    ReactiveFormsModule,
    Bind,
    InputText,
    InputPassword,
    IconField,
    InputIcon,
    Message,
    Checkbox,
  ],
})
export class LoginComponent implements OnInit, OnDestroy {
  private formBuilder = inject(UntypedFormBuilder);
  private userRepository = inject(UserRepository);
  private notifyService = inject(NotifyService);
  private userService = inject(UserService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private httpConfig = inject(AuthHttpConfig);
  private returnUrlService = inject(ReturnUrlService);
  private permissionService = inject(NgxPermissionsService);
  private document = inject<Document>(DOCUMENT);

  public user: User = null;
  public loginForm: UntypedFormGroup;
  public registerForm: UntypedFormGroup;
  public resetPasswordForm: UntypedFormGroup;
  public joinForm: UntypedFormGroup;
  public activateForm: UntypedFormGroup;
  public twoFactorForm: UntypedFormGroup;
  public resetPasswordRequestForm: UntypedFormGroup;
  public loading = false;
  public formType = formType;
  public currentFormType = formType.login;
  public subscription = new Subscription();
  public registrationInstructions = false;
  public info = {
    header: "",
    message: "",
  };
  // Reveal state per password input — `pInputPassword` only flips the input's
  // type, the eye that drives it is ours.
  public masked = {
    login: true,
    reset: true,
    resetConfirm: true,
    join: true,
    joinConfirm: true,
    activate: true,
    activateConfirm: true,
  };
  public readonly passwordRequirements =
    "Use at least 7 characters, with one lowercase letter, one capital letter and one number.";

  private token: string = null;

  ngOnInit() {
    // Turns off the app shell's header band, page gutter and pinned footer for
    // as long as this page is mounted — see layout/_signin.scss.
    this.document.body.classList.add("signin-page");
    this.getUser();
    this.createForms();
    this.checkRoute();
  }

  ngOnDestroy() {
    this.document.body.classList.remove("signin-page");
    this.subscription.unsubscribe();
  }

  // `confirmed` and `info` both title the panel with a message the backend (or a
  // failed token check) produced, so they read `info` rather than the constant.
  public get heading(): SignInHeading {
    if (
      this.currentFormType === formType.info ||
      this.currentFormType === formType.confirmed
    ) {
      return { title: this.info.header, subtitle: this.info.message };
    }

    return signInHeading[this.currentFormType];
  }

  public isInvalid(form: UntypedFormGroup, controlName: string): boolean {
    const control = form.get(controlName);

    return control.invalid && (control.dirty || control.touched);
  }

  public urlContains(url: string): boolean {
    return this.router.url.indexOf(url) > -1;
  }

  public checkRoute() {
    if (this.urlContains("login")) {
      this.currentFormType = formType.login;
    }

    if (this.urlContains("confirmed")) {
      this.info.header = "Account has been confirmed.";
      this.info.message = "";
      this.currentFormType = formType.confirmed;
    }

    if (this.urlContains("register")) {
      this.currentFormType = formType.register;
    }

    if (this.urlContains("registered")) {
      this.currentFormType = formType.registered;
    }

    if (this.urlContains("reset")) {
      this.currentFormType = formType.reset;
    }

    if (this.urlContains("join")) {
      this.currentFormType = formType.join;
    }

    if (this.urlContains("activate")) {
      this.currentFormType = formType.activate;
    }

    if (this.urlContains("logout")) {
      this.handleLogout();
    }

    if (this.urlContains("exit")) {
      this.handleExitImpersonateMode();
    }

    this.subscription.add(
      this.route.params.subscribe((params) => {
        this.token = params["token"] || null;
        if (this.token) {
          if (this.currentFormType == formType.join) {
            this.checkJoinToken(this.token);
          } else if (this.currentFormType == formType.register) {
            this.checkResetToken(this.token);
          }
        }
      }),
    );
  }

  public createForms() {
    this.loginForm = this.formBuilder.group({
      username: ["", Validators.required],
      password: ["", Validators.required],
    });
    this.resetPasswordRequestForm = this.formBuilder.group({
      email: ["", [Validators.required, Validators.email]],
    });
    this.resetPasswordForm = this.formBuilder.group({
      password: ["", [Validators.required, PasswordValidator.strong]],
      passwordConfirm: ["", Validators.required],
    });
    this.joinForm = this.formBuilder.group({
      password: ["", [Validators.required, PasswordValidator.strong]],
      passwordConfirm: ["", Validators.required],
    });
    this.activateForm = this.formBuilder.group({
      password: ["", [Validators.required, PasswordValidator.strong]],
      passwordConfirm: ["", Validators.required],
      termsAgreement: [false],
    });
    this.registerForm = this.formBuilder.group({
      organizationName: ["", Validators.required],
      firstName: ["", Validators.required],
      lastName: ["", Validators.required],
      email: ["", [Validators.required, Validators.email]],
      notes: [""],
    });
    this.twoFactorForm = this.formBuilder.group({
      code: ["", [Validators.required, Validators.pattern(/^\d{6}$/)]],
    });
  }

  public switchFormType(param: string) {
    this.currentFormType = param;
    this.registrationInstructions = false;

    if (param === formType.requestReset) {
      this.resetPasswordRequestForm.patchValue({
        email: this.loginForm.controls.username.value,
      });
      this.resetPasswordRequestForm.updateValueAndValidity();
    }

    if (param === formType.login) {
      this.loginForm.patchValue({
        username: this.resetPasswordRequestForm.controls.email.value,
        password: this.resetPasswordForm.controls.password.value,
      });
      this.loginForm.updateValueAndValidity();
    }
  }

  public passwordsMatch(form: UntypedFormGroup): boolean {
    if (
      form.controls.password &&
      form.controls.passwordConfirm &&
      form.controls.password.value === form.controls.passwordConfirm.value
    ) {
      return true;
    }

    this.notifyService.toast(TOAST.WARN, "Passwords don't match");

    return false;
  }

  public logIn() {
    if (!this.loginForm.valid) {
      this.handleInvalidForm(this.loginForm);

      return;
    }

    this.loading = true;
    this.subscription.add(
      this.userRepository.login(this.loginForm.value).subscribe({
        next: (loginResponse) => {
          this.loading = false;
          if (
            Object.prototype.hasOwnProperty.call(loginResponse, "login") &&
            loginResponse["login"] === "success" &&
            Object.prototype.hasOwnProperty.call(
              loginResponse,
              "two_factor_complete",
            ) &&
            loginResponse["two_factor_complete"] === false
          ) {
            this.currentFormType = formType.twoFactor;
          } else if (
            Object.prototype.hasOwnProperty.call(loginResponse, "message")
          ) {
            this.notifyService.toast(
              TOAST.INFO,
              <string>new BackendErrorPipe().transform(loginResponse, "string"),
            );
          } else if (
            Object.prototype.hasOwnProperty.call(loginResponse, "roles") &&
            loginResponse["roles"].indexOf(role.user) === -1
          ) {
            this.notifyService.toast(
              TOAST.INFO,
              "You are not authorized to access the application",
            );
          } else if (
            Object.prototype.hasOwnProperty.call(loginResponse, "token")
          ) {
            this.loading = false;
            this.userService.clearUser();
            this.userService.loginUser(loginResponse);
            this.navigateAfterSignIn();
          }
        },
        error: (err) => {
          this.handleError(err);
        },
      }),
    );
  }

  public verifyTwoFactor() {
    if (!this.twoFactorForm.valid) {
      this.handleInvalidForm(this.twoFactorForm);
      return;
    }

    this.loading = true;
    const twoFactorData = {
      code: this.twoFactorForm.value.code,
    };

    this.subscription.add(
      this.userRepository.verifyTwoFactor(twoFactorData).subscribe({
        next: (loginResponse) => {
          this.loading = false;
          if (Object.prototype.hasOwnProperty.call(loginResponse, "token")) {
            this.userService.clearUser();
            this.userService.loginUser(loginResponse);
            this.navigateAfterSignIn();
          } else {
            this.notifyService.toast(
              TOAST.ERROR,
              "Invalid two-factor authentication code",
            );
          }
        },
        error: (err) => {
          this.handleError(err);
        },
      }),
    );
  }

  public resetPasswordRequest() {
    if (!this.resetPasswordRequestForm.valid) {
      this.handleInvalidForm(this.resetPasswordRequestForm);

      return;
    }

    this.loading = true;
    this.subscription.add(
      this.userRepository
        .resetPasswordRequest(this.resetPasswordRequestForm.value)
        .subscribe({
          next: (response) => {
            this.loading = false;
            this.info.header = "Request has been sent.";
            this.info.message =
              "Please check your email and follow the instructions to reset your password.";
            this.switchFormType(formType.info);
          },
          error: (err) => {
            this.handleError(err);
          },
        }),
    );
  }

  public resetPassword() {
    if (!this.resetPasswordForm.valid) {
      this.handleInvalidForm(this.resetPasswordForm);

      return;
    }

    if (!this.passwordsMatch(this.resetPasswordForm)) {
      return;
    }

    this.loading = true;
    this.subscription.add(
      this.userRepository
        .resetPassword(this.resetPasswordForm.value, this.token)
        .subscribe({
          next: (response) => {
            this.loading = false;
            this.currentFormType = formType.login;

            if (Object.prototype.hasOwnProperty.call(response, "id")) {
              this.loginForm.patchValue({
                username: response["username"],
                password: this.resetPasswordForm.controls.password.value,
              });
            }
          },
          error: (error) => {
            this.handleError(error);
          },
        }),
    );
  }

  public join() {
    if (!this.joinForm.valid) {
      this.handleInvalidForm(this.joinForm);

      return;
    }

    if (!this.passwordsMatch(this.joinForm)) {
      return;
    }

    this.loading = true;
    this.subscription.add(
      this.userRepository.join(this.joinForm.value, this.token).subscribe({
        next: (response) => {
          this.loading = false;
          this.currentFormType = formType.login;
          if (Object.prototype.hasOwnProperty.call(response, "id")) {
            this.loginForm.patchValue({
              username: response["username"],
              password: this.joinForm.controls.password.value,
            });
            this.logIn();
          }
        },
        error: (error) => {
          this.handleError(error);
        },
      }),
    );
  }

  public activate() {
    if (!this.activateForm.valid) {
      this.handleInvalidForm(this.activateForm);

      return;
    }

    if (!this.passwordsMatch(this.activateForm)) {
      return;
    }

    this.loading = true;
    this.subscription.add(
      this.userRepository
        .activate(this.activateForm.value, this.token)
        .subscribe({
          next: (response) => {
            this.loading = false;
            this.currentFormType = formType.login;
            if (Object.prototype.hasOwnProperty.call(response, "id")) {
              this.loginForm.patchValue({
                username: response["username"],
                password: this.activateForm.controls.password.value,
              });
              this.logIn();
            }
          },
          error: (error) => {
            this.handleError(error);
          },
        }),
    );
  }

  public registerUser() {
    if (!this.registerForm.valid) {
      this.handleInvalidForm(this.registerForm);

      return;
    }

    this.loading = true;
    this.subscription.add(
      this.userRepository.register(this.registerForm.value).subscribe({
        next: (registrationResponse) => {
          this.loading = false;
          this.switchFormType(formType.registered);
          this.router.navigate(["registered"]);
        },
        error: (err) => {
          this.handleError(err);
        },
      }),
    );
  }

  public checkJoinToken(token: string): void {
    this.loading = true;
    this.subscription.add(
      this.userRepository.checkJoinToken(this.token).subscribe({
        next: (response) => {
          this.loading = false;
        },
        error: (err) => {
          this.info.header = "Token is no longer valid.";
          this.info.message =
            "Please contact the administrator, and ask the resend the invitation.";
          this.switchFormType(formType.info);
        },
      }),
    );
  }

  public checkResetToken(token: string): void {
    this.loading = true;
    this.subscription.add(
      this.userRepository.checkResetToken(this.token).subscribe({
        next: (response) => {
          this.loading = false;
        },
        error: (err) => {
          this.info.header = "Token is no longer valid.";
          this.info.message = "Please use the reset form again.";
          this.switchFormType(formType.info);
        },
      }),
    );
  }

  // Only the states with no form of their own. Everywhere else the link sits
  // under the last field, inside the form it belongs to.
  public isSignInVisible() {
    return (
      this.currentFormType === formType.confirmed ||
      this.currentFormType === formType.info
    );
  }

  // The user goes back to the page a forced logout interrupted, or the one
  // they opened while signed out; with nothing to go back to they land on the
  // dashboard. A URL their role does not allow is bounced by the route guards,
  // which land on the dashboard anyway.
  private navigateAfterSignIn(): void {
    const returnUrl = this.returnUrlService.consume();

    if (!returnUrl) {
      this.router.navigate(["dashboard"], { replaceUrl: true });

      return;
    }

    // The guards on the restored route read ngx-permissions, and those are
    // only filled once the user request that AppComponent fires on sign-in
    // resolves. Navigating before that bounces even an allowed URL to "/".
    this.userService.getUser().then(
      (user) => {
        if (!user) {
          this.router.navigate(["dashboard"], { replaceUrl: true });

          return;
        }

        this.permissionService.loadPermissions(user.getRoles());
        this.router.navigateByUrl(returnUrl, { replaceUrl: true });
      },
      () => this.router.navigate(["dashboard"], { replaceUrl: true }),
    );
  }

  private handleLogout() {
    this.returnUrlService.clear();
    this.userService.serviceUserChange.emit(0);
    this.userService.clearUser();
  }

  private handleExitImpersonateMode() {
    this.httpConfig.removeImpersonatedUsername();
    document.location.href = "/";
  }

  private handleError(err) {
    this.loading = false;
    this.notifyService.toast(
      TOAST.ERROR,
      <string>new BackendErrorPipe().transform(err, "string") ||
        "An error occurred",
    );
  }

  // Every field now carries its own message under the control, so an invalid
  // submit only has to reveal them — the old blanket "Invalid data" toast said
  // less than the form itself does.
  private handleInvalidForm(form: UntypedFormGroup) {
    form.markAllAsTouched();
  }

  private getUser(): void {
    this.userService.getUser().then((user) => (this.user = user), noop);
  }
}
